<template>
    <span v-if="account.plan_id == 1" class="d-block mt-5 text-center" style="font-size: 12px; line-height: 2;">
        Collaboration is not available on the free plan.
        <router-link :to="{ name: 'account.billing.changePlan' }"
                     style="text-decoration: underline; color: inherit;"
        >Upgrade</router-link> unlock collaboration features.
    </span>

    <div v-else-if="!canAddSeats">
        <span v-if="isCurrentPlanPro" class="d-block mt-5 text-center" style="font-size: 12px; line-height: 2;">
            You can't invite additional users.
            <router-link :to="{ name: 'account.billing.changePlan' }"
                         style="text-decoration: underline; color: inherit;"
            >Upgrade</router-link> to Team plan to add more.
        </span>

        <span v-if="isCurrentPlanTeam" class="d-block mt-5 text-center" style="font-size: 12px; line-height: 2;">You can't invite additional users. <a
            href="mailto:support@fylm.ai?subject=Enterprise plan application" style="text-decoration: underline; color: inherit;"
        >Contact us</a> for Enterprise plan.</span>
    </div>

    <div v-else class="color-gray">
        <div class="d-flex mb-4">
            <span class="dashboard-column-title text-left" style="flex: 5;">Email address</span>
            <span class="dashboard-column-title text-center" style="flex: 2;">Role</span>
        </div>

        <div v-for="(user, index) in form.users" :key="index" class="d-flex mb-1" no-gutters>
            <v-text-field
                :ref="`inputInvitee${index}`"
                v-model="form.users[index].email"
                :disabled="!canAddSeats"
                :error-messages="form.$errors[`users.${index}.email`]"
                class="color-gray mr-5"
                style="flex: 5;"
                dark
                label="Email address"
                outlined
                type="email"
                @blur="form.validate(`users.${index}.email`)"
                @input="delete form.$errors[`users.${index}.email`]; formTouched = true;"
                @keyup.enter="addLine"
            />

            <v-select
                v-model="user.role_id"
                :disabled="!canAddSeats"
                :items="availableRolesToAssign"
                :menu-props="{ offsetY: true, contentClass: 'dashboard-select' }"
                class="dashboard-select"
                style="flex: 2;"
                dark
                item-text="name"
                item-value="id"
                outlined
            />
        </div>

        <v-row class="d-flex justify-content-between mb-10">
            <v-btn
                :disabled="!canAddSeats"
                :ripple="false"
                class="color-gray bg-transparent d-flex"
                color="#181818"
                dark
                depressed
                rounded
                @click="addLine"
            >
                + Add line
            </v-btn>
            <v-btn
                :disabled="!canAddSeats"
                :ripple="false"
                class="color-gray bg-transparent"
                color="#181818"
                dark
                depressed
                rounded
                @click="removeLine"
            >
                Remove line -
            </v-btn>
        </v-row>

        <v-row v-if="displayAdditionalCostMessage" class="mb-4">
            <v-col sm="3" class="dashboard-text">
                <span>Additional cost</span>
            </v-col>
            <v-col sm="9" class="dashboard-text">
                <div class="mb-1">
                    <span class="color-primary font-bold">
                        <v-progress-circular v-if="fetchingUpdateEstimation" indeterminate color="var(--primary)" size="12"
                                             width="2" />
                        <template v-else>{{ estimatedCostDisplay }}</template>
                    </span>
                    paid today for {{ additionalChargingSeats }} additional
                    {{ additionalChargingSeats == 1 ? "user" : "users" }} until
                    {{ Moment(currentPlan.fastspring.nextChargeDate).format("MMMM D, YYYY") }}.
                </div>
                <div class="mb-1">
                    <span class="color-primary font-bold">
                        <v-progress-circular v-if="fetchingUpdateEstimation" indeterminate color="var(--primary)" size="12"
                                             width="2" />
                        <template v-else>{{ nextChargeAmountDisplay }}</template>
                    </span>
                    for every additional
                    <template v-if="currentPlan.id == 2 || currentPlan.id == 4">month</template>
                    <template v-if="currentPlan.id == 3 || currentPlan.id == 5">year</template>
                    from {{ Moment(currentPlan.fastspring.nextChargeDate).format("MMMM D, YYYY") }}.
                </div>
                <span v-if="!isSubscriptionInTrial">
                    You will pay prorated amount today and pay the new subscription amount on your next billing date.
                </span>
            </v-col>
        </v-row>

        <v-row v-if="formTouched" class="center">
            <v-col>
                <v-btn :loading="sendLoading" block class="dashboard-button bg-primary color-white" depressed large
                       @click="submitInvitations"
                >
                        <span v-if="!sendSubmitted" class="color-white">
                            {{
                                additionalChargingSeats > 0 ? "Accept additional charge and send invitations" : "send invitations"
                            }}
                        </span>
                    <v-icon v-if="sendSubmitted" color="white" medium>mdi mdi-check-circle</v-icon>
                </v-btn>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import * as rules from "vuelidate/lib/validators";
import { mapGetters, mapState } from "vuex";
import CanMixin from "@/mixins/CanMixin";

export default {
    name: "AddUsers",
    mixins: [CanMixin],

	props: {
		project: {
			type: Object,
			default: () => {
				return {
					id: null,
				}
			}
		},
	},

    data() {
        return {
            sendLoading: false,
            sendSubmitted: false,
            formTouched: false,
            fetchingUpdateEstimation: false,
            estimatedCostDisplay: '',
            nextChargeAmountDisplay: '',
            form: new Form(this, "form", {
                users: [
                    {
                        email: null,
                        role_id: 5,
                    },
                ],
            }),
        };
    },

    validations: {
        form: {
            users: {
                $each: {
                    email: { email: rules.email, required: rules.required },
                },
            },
        },
    },

    created() {
        this.resetForm();
    },

    computed: {
        ...mapState("Roles", ["rolesAvailable"]),
        ...mapState("User", ["account", "currentPlan"]),
        ...mapGetters("User", ["isCurrentPlanFree", "isCurrentPlanLowerThanTeam"]),
        ...mapState("Organizations", ["users", 'pendingUsers']),

        placeholderUser() {
            return {
                email: null,
                role_id: 5,
            }
        },

        canAddSeats() {
            if (this.isCurrentPlanPro) {
                if (this.currentPlan.users.added + 1 == 5) return false;
                else return true;
            } else if (this.isCurrentPlanTeam) {
                if (this.currentPlan.users.added + 1 == 10) return false;
                else return true;
            } else return false;
        },

        isCurrentPlanPro() {
            if (this.currentPlan && (this.currentPlan.id == 2 || this.currentPlan.id == 3)) return true;
            else return false;
        },

        isCurrentPlanTeam() {
            if (this.currentPlan && (this.currentPlan.id == 4 || this.currentPlan.id == 5)) return true;
            else return false;
        },

        additionalChargingSeats() {
            if (this.isCurrentPlanTeam) {
                if (this.currentPlan.users.added + 1 > 3) return this.form.users.length;
                else return this.currentPlan.users.added + 1 - 3 + this.form.users.length;
            } else {
                return this.form.users.length;
            }
        },

        availableRolesToAssign() {
            if (this.isCurrentPlanLowerThanTeam) return this.rolesAvailable.filter(role => role.id > 4);
            else return this.rolesAvailable;
        },

        includedUsers() {
            if (this.account.plan_id == 2 || this.account.plan_id == 3) {
                return 1;
            }
            if (this.account.plan_id == 4 || this.account.plan_id == 5) {
                return 3;
            }
            return 0;
        },

        displayAdditionalCostMessage() {
            if (this.formTouched && this.form.users.length > 0) {
                this.getSubscriptionUpdateEstimate();

                if (this.isCurrentPlanTeam) {
                    if (this.currentPlan.users.added + 1 + this.form.users.length > 3) return true;
                    else return false;
                }

                if (this.isCurrentPlanPro) return true;
                else return false;
            } else return false;
        },

        isSubscriptionInTrial() {
            return this.currentPlan.fastspring.state == "trial" ? true : false;
        },
    },

    methods: {
        resetForm() {
            this.formTouched = false;
            this.form.users = [{ ...this.placeholderUser }];
        },

        async submitInvitations() {
            // checking if double emails in form or email alraedy invited
            let filteredList = {
                users: [],
            };

            this.form.users.forEach(user => {
                if (!filteredList.users.map(e => e.email).includes(user.email)) {
                    if (!this.pendingUsers.map(e => e.user.email).includes(user.email)) {
                        filteredList.users.push(user);
                    }
                }
            });

            if (filteredList.users.length == 0) {
                this.resetForm();
                return;
            }

			if (this.project.id) {
				filteredList.project_id = this.project.id;
			}

            this.sendLoading = true;

            await this.$store.dispatch('Organizations/inviteUsers', filteredList);
            await this.$store.dispatch("Organizations/fetchPendingUsers");
            await this.$store.dispatch("User/fetchCurrentPlan");

            this.sendLoading = false;
            this.sendSubmitted = true;

            setTimeout(() => {
                this.sendSubmitted = false;
            }, 3000);

            this.resetForm();
        },

        addLine() {
            const planId = this.account.plan_id;
            const curUsersNum = this.form.users.length;

            if (planId == 1) {
                this.$snackbar("You have reached the maximum available number of users.", "error");
                return;
            }

            if ((planId == 2 || planId == 3) && curUsersNum + this.currentPlan.users.added + 1 >= 5) {
                this.$snackbar(`Please upgrade to Team plan to have more than 5 users.`, "error");
                return;
            }

            if ((planId == 4 || planId == 5) && curUsersNum + this.currentPlan.users.added + 1 >= 10) {
                this.$snackbar(`Please upgrade to Enterprise plan to have more than 10 users.`, "error");
                return;
            }

            this.form.users.push({ ...this.placeholderUser });

            this.$nextTick(() => {
                this.$refs[`inputInvitee${this.form.users.length - 1}`][0].focus();
            });

            this.getSubscriptionUpdateEstimate();
        },

        removeLine() {
            if (this.form.users.length > 1) {
                this.form.users.splice(-1, 1);
                this.getSubscriptionUpdateEstimate();
            }
        },

        async getSubscriptionUpdateEstimate() {
            this.fetchingUpdateEstimation = true;
            const numberOfAddons = this.form.users.length;
            const res = await this.$apiService.get(`accounts/${this.account.id}/update-plan/estimate`, {
                params: {
                    number_of_addons: numberOfAddons
                }
            });
            this.estimatedCostDisplay = res.data.amountDue.totalAmountDueDisplay;
            this.nextChargeAmountDisplay = res.data.amountDue.nextChargeAmountDisplay;
            this.fetchingUpdateEstimation = false;
        }
    },
}
</script>